import { FC } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { DataIcon } from '@/components/icons/DataIcon';
import { UserPlus02Icon } from '@/components/icons/UserPlus02Icon';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { getIntegrationProviderDisplayName } from '@/modules/assetProviderIntegrations/shared/constants';
import { useIsAssetProviderIntegrationEnabled } from '@/modules/assetProviderIntegrations/shared/hooks/useEnabledAssetsIntegrations';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { ROUTE_KEYS } from '@/navigation/constants';

export const ManageExternalDataButton: FC = () => {
  const canCreateClients = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_CREATE_CLIENTS
  );
  const { navigate } = useNavigateToRoute();
  const { integrationsConnected } = useIsAssetProviderIntegrationEnabled();

  if (!canCreateClients || integrationsConnected.length === 0) {
    return null;
  }

  const hasMultipleIntegrations = integrationsConnected.length > 1;

  return (
    <ButtonWithPopover
      label="Manage external data"
      data-testid="manage-external-data"
      variant="secondary"
      size="sm"
      startIcon={DataIcon}
      popperVariant="menuBelow"
    >
      {integrationsConnected.flatMap((integration) => {
        const integrationName = getIntegrationProviderDisplayName(integration);
        const importNewClientsLabel = hasMultipleIntegrations
          ? `Import new clients`
          : `Import new clients from ${integrationName}`;

        const items = [
          <MenuItem
            key={integration}
            label={importNewClientsLabel}
            onClick={() =>
              navigate(ROUTE_KEYS.IMPORT_INTEGRATION_CLIENTS, {
                flowType: integration,
              })
            }
          />,
        ];

        if (hasMultipleIntegrations) {
          items.unshift(
            <SelectItemGroupLabel
              key={`${integration}-label`}
              label={integrationName}
            />
          );
        }

        return items;
      })}
      {hasMultipleIntegrations && <SelectItemGroupLabel label="Other" />}
      <MenuItem
        label="Upload CSV with valuation data"
        onClick={() => navigate(ROUTE_KEYS.IMPORT_CSV_VALUATIONS, {})}
      />
    </ButtonWithPopover>
  );
};

export interface CreateClientButtonProps {
  onClick: () => void;
}

export const CreateClientButton: FC<CreateClientButtonProps> = ({
  onClick,
}) => {
  const canCreateClients = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_CREATE_CLIENTS
  );

  if (!canCreateClients) return null;

  return (
    <Button
      size="sm"
      variant="primary"
      onClick={onClick}
      startIcon={UserPlus02Icon}
    >
      Add client
    </Button>
  );
};
